import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const links = [
  { to: "/about", label: "About Alex" },
  { to: "/messy-middle", label: "The Messy Middle" },
  { to: "/organisations", label: "For Organisations" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const darkHero = ["/", "/messy-middle", "/organisations"].includes(pathname);
  const onDark = darkHero && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open
          ? "bg-paper/90 backdrop-blur-md border-line"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`font-serif text-lg md:text-xl tracking-tight leading-none transition-colors duration-500 ${onDark ? "text-paper" : "text-ink"}`}
        >
          People <span className={`italic ${onDark ? "text-clay" : "text-moss"}`}>Growing</span> People
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive
                    ? "text-clay"
                    : onDark
                      ? "text-paper/70 hover:text-paper"
                      : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/work-with-alex"
            data-testid="nav-cta-work-with-alex"
            className={`group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors duration-300 ${
              onDark
                ? "border border-paper/40 bg-ink/30 backdrop-blur-sm text-paper hover:bg-paper hover:text-ink"
                : "bg-ink text-paper hover:bg-moss"
            }`}
          >
            Work With Alex
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 -mr-2 ${onDark ? "text-paper" : "text-ink"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-line bg-paper"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="font-serif text-3xl tracking-tight"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/work-with-alex"
                data-testid="nav-mobile-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-6 py-4"
              >
                Work With Alex
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
